<div
     snAssistantSurface="oauthApp"
     i18n-surfaceTitle="@@oauthApp.surface"
     surfaceTitle="OAuth application" class="oauth-edit-container">
  <div class="top-panel">
    <div class="top-panel-container">
      <div class="panel-left-section">
        <p-breadcrumb
          [model]="breadcrumbItems"
          styleClass="custom-breadcrumb compact-breadcrumb"
          [style]="{'--p-breadcrumb-padding': '0', '--p-breadcrumb-item-hover-color': 'var(--p-primary-color)'}">
        </p-breadcrumb>
      </div>
      <div class="panel-right-section">
        @if (isEditMode && applicationId) {
          <p-button
            i18n-label="@@oauthApps.delete.button"
            label="Delete"
            icon="pi pi-trash"
            severity="danger"
            [outlined]="true"
            size="small"
            (click)="askDelete()"
            snAssistantAction="delete"
            actionDangerous
            actionHint="Deletes the application. Every integration using its client id stops working at once.">
          </p-button>
        }
        <p-button
          i18n-label="@@oauthApps.backToDevelopers"
          label="Back"
          icon="pi pi-arrow-left"
          severity="secondary"
          [outlined]="true"
          size="small"
          (click)="goBack()">
        </p-button>
      </div>
    </div>
  </div>

  <div class="page-container">
    <p-card class="edit-card">
      <div class="header-section">
        <h2>{{ headerTitle }}</h2>
        <p class="header-description" i18n="@@oauthApps.headerDescription">
          OAuth applications let third-party services integrate via the authorization_code flow.
        </p>
      </div>

      @if (isLoading) {
        <div class="loading-container">
          <p-progressSpinner strokeWidth="3" [style]="{'width': '50px', 'height': '50px'}"></p-progressSpinner>
          <p i18n="@@oauthApps.loading">Loading application...</p>
        </div>
      }

      @if (!isLoading && mode === 'form') {
        <div class="form-container">
          <div class="form-field">
            <label class="field-label" i18n="@@oauthApps.name.label">Name *</label>
            <p class="field-description" i18n="@@oauthApps.name.description">Internal name of the application, visible to owners only.</p>
            <input
              [snAssistantId]="F.name"
              pInputText
              name="name"
          [(ngModel)]="name"
              i18n-placeholder="@@oauthApps.name.placeholder"
              placeholder="My App"
              class="w-full">
          </div>

          <div class="form-field">
            <label class="field-label" i18n="@@oauthApps.redirectUris.label">Redirect URIs *</label>
            <p class="field-description" i18n="@@oauthApps.redirectUris.description">
              URLs where Keycloak will redirect the user after authorization. Use HTTPS (http://localhost is allowed for development).
            </p>
            <div class="uris-list">
              @for (uri of redirectUris; track trackByIndex($index); let i = $index) {
                <div class="uri-row">
                  <input
                    pInputText
                    [(ngModel)]="redirectUris[i]"
                    [snAssistantId]="'redirectUri' + (i + 1)"
                    [snAssistantLabel]="'Redirect URI ' + (i + 1)"
                    placeholder="https://example.com/oauth/callback"
                    class="w-full">
                  <p-button
                    icon="pi pi-trash"
                    severity="danger"
                    [text]="true"
                    [rounded]="true"
                    [disabled]="redirectUris.length === 1 && !redirectUris[0]"
                    i18n-pTooltip="@@oauthApps.redirectUris.remove"
                    pTooltip="Remove URI"
                    (click)="removeRedirectUri(i)"
                    [snAssistantAction]="'removeRedirectUri.' + (i + 1)"
                    [actionLabel]="'Remove redirect URI ' + (i + 1)"
                    i18n-ariaLabel="@@oauthApps.redirectUris.remove"
                    ariaLabel="Remove URI">
                  </p-button>
                </div>
              }
              <p-button
                i18n-label="@@oauthApps.redirectUris.add"
                label="Add URI"
                icon="pi pi-plus"
                severity="secondary"
                [outlined]="true"
                size="small"
                (click)="addRedirectUri()"
                snAssistantAction="addRedirectUri"
                actionHint="Adds one more empty redirect URI row.">
              </p-button>
            </div>
          </div>

          @if (isEditMode && clientIdShown) {
            <div class="form-field">
              <label class="field-label" i18n="@@oauthApps.clientId.label">Client ID</label>
              <div class="readonly-row">
                <code class="readonly-value">{{ clientIdShown }}</code>
                <p-button
                  icon="pi pi-copy"
                  severity="secondary"
                  [outlined]="true"
                  size="small"
                  (click)="copy(clientIdShown, 'Client ID')"
                  i18n-pTooltip="@@common.copyToClipboard"
                  pTooltip="Copy to clipboard"
                  i18n-ariaLabel="@@common.copyToClipboard"
                  ariaLabel="Copy to clipboard">
                </p-button>
              </div>
            </div>

            <div class="form-field">
              <p-button
                i18n-label="@@oauthApps.regenerate.button"
                label="Generate new secret"
                icon="pi pi-refresh"
                severity="warn"
                [outlined]="true"
                (click)="askRegenerate()"
                snAssistantAction="regenerateSecret"
                actionDangerous
                actionHint="Issues a new client secret. The old one stops working immediately, and every integration holding it breaks until it is updated.">
              </p-button>
              <p class="field-description" i18n="@@oauthApps.regenerate.description">
                The old secret stops working immediately after generating a new one.
              </p>
            </div>
          }
        </div>

        <div class="action-buttons">
          <p-button
            type="button"
            i18n-label="@@common.cancel"
            label="Cancel"
            severity="secondary"
            [outlined]="true"
            icon="pi pi-arrow-left"
            (click)="goBack()">
          </p-button>
          <p-button
            type="button"
            [label]="isEditMode ? saveBtnEditLabel : saveBtnCreateLabel"
            [icon]="isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
            [disabled]="!canSave"
            [loading]="isSaving"
            (click)="save()"
            snAssistantAction="save"
            actionHint="Writes the application — its name and redirect URIs.">
          </p-button>
        </div>
      }

      @if (mode === 'secret') {
        <div class="secret-screen">
          <p-message severity="warn" [closable]="false" styleClass="w-full">
            <div class="secret-warning-content">
              <i class="pi pi-exclamation-triangle"></i>
              <div>
                <strong i18n="@@oauthApps.secret.warningTitle">Save the secret now</strong>
                <p i18n="@@oauthApps.secret.warningBody">
                  This is the only time we show the client_secret. Copy it somewhere safe — once you leave this page it cannot be retrieved, only regenerated.
                </p>
              </div>
            </div>
          </p-message>

          <div class="form-field">
            <label class="field-label" i18n="@@oauthApps.clientId.label">Client ID</label>
            <div class="readonly-row">
              <code class="readonly-value">{{ clientIdShown }}</code>
              <p-button
                icon="pi pi-copy"
                severity="secondary"
                [outlined]="true"
                (click)="copy(clientIdShown, 'Client ID')"
                i18n-pTooltip="@@common.copyToClipboard"
                pTooltip="Copy to clipboard"
                i18n-ariaLabel="@@common.copyToClipboard"
                ariaLabel="Copy to clipboard">
              </p-button>
            </div>
          </div>

          <div class="form-field">
            <label class="field-label" i18n="@@oauthApps.clientSecret.label">Client Secret</label>
            <div class="readonly-row">
              <code class="readonly-value">{{ clientSecretShown }}</code>
              <p-button
                icon="pi pi-copy"
                severity="secondary"
                [outlined]="true"
                (click)="copy(clientSecretShown, 'Client Secret')"
                i18n-pTooltip="@@common.copyToClipboard"
                pTooltip="Copy to clipboard"
                i18n-ariaLabel="@@common.copyToClipboard"
                ariaLabel="Copy to clipboard">
              </p-button>
            </div>
          </div>

          <div class="action-buttons">
            <p-button
              type="button"
              i18n-label="@@oauthApps.secret.done"
              label="Done"
              icon="pi pi-check"
              (click)="goBack()">
            </p-button>
          </div>
        </div>
      }
    </p-card>
  </div>
</div>

<app-delete-confirmation
  [(visible)]="deleteDialogVisible"
  [title]="deleteTitle"
  [subtitle]="deleteSubtitle"
  [deleting]="isDeleting"
  (confirm)="onDeleteConfirm()"
  (cancel)="onDeleteCancel()">
</app-delete-confirmation>

<app-delete-confirmation
  [(visible)]="regenerateDialogVisible"
  [title]="regenerateTitle"
  [subtitle]="regenerateSubtitle"
  [deleting]="isRegenerating"
  (confirm)="onRegenerateConfirm()"
  (cancel)="onRegenerateCancel()">
</app-delete-confirmation>
